import { computed } from "vue";
import { useBodyScrollLock } from "#ce/composables/useBodyScrollLock";
import { toBoolean } from "#ce/utils/props";

/** @typedef {import("../props.js").SpDialogProps} SpDialogProps */

/**
 * Page scroll-lock policy for sp-dialog. Wraps the shared body scroll lock with
 * the dialog's rule for when to engage it: when `lockScroll` is unset, modal
 * dialogs (with backdrop) lock the page and no-backdrop (popover-style) dialogs
 * do not; `lockScroll` overrides either way. `lock()` is a no-op when the policy
 * says not to, so the open/close paths can call it unconditionally.
 *
 * @see https://github.com/whatwg/html/issues/7732
 * @param {SpDialogProps} props - The sp-dialog props.
 * @returns {{ lock: () => void, unlock: () => void }}
 */
export function useDialogScrollLock(props) {
  const { lock, unlock } = useBodyScrollLock();

  const shouldLock = computed(() => {
    if (props.lockScroll === undefined) {
      return !toBoolean(props.noBackdrop);
    }
    return toBoolean(props.lockScroll);
  });

  function lockIfEnabled() {
    if (shouldLock.value) {
      lock();
    }
  }

  return { lock: lockIfEnabled, unlock };
}
